/*
 * The small decisions every screen that shows a person has to make the same way: what a person is
 * called, what their state reads as, the line under their name, how a filter matches them, and the
 * order a list of them appears in. What a person may do — their standing — is `standing.ts`'s, which
 * these decisions read. They live here rather than in a component because the directory, the groups
 * section, the people picker and the administration table all make them, and three of the four are
 * not even in the same area.
 *
 * What a person *published* about themselves is not here: a status is a feature with a palette,
 * presets and durations of its own, and all of it is said once in `status.ts`.
 */

import { t } from "../i18n/index.ts"
import type { PublicUser } from "../store/model.ts"
import { accessLabel, isGuest, standingLabel, type StandingSource } from "./standing.ts"

/**
 * The line under a person's name on the profile and the hover card: what the account is and its
 * handle. An account whose standing this build has no word for is named by its handle alone, rather
 * than by a line that opens with a separator.
 */
export function profileSubtitle(user: StandingSource & Pick<PublicUser, "handle">): string {
	const access = accessLabel(user)
	return access === ""
		? t("people.card.meta", { handle: user.handle })
		: t("people.profile.subtitle", { access, handle: user.handle })
}

/**
 * What a row says beside the name when the account is not an ordinary live team member: an account an
 * admin has deactivated, a bot, or a guest. A guest wears it on every chip — the hover card, the
 * directory, a channel's members — because the others in a conversation should see that this person
 * is not on the team. A team member and an administrator get no badge here; the directory marks an
 * administrator itself.
 */
export function accountBadge(user: PublicUser): string | undefined {
	if (user.state === "deactivated") return t("people.state.deactivated")
	if (user.kind === "bot") return t("people.kind.bot")
	if (isGuest(user)) return standingLabel("guest")
	return undefined
}

/**
 * The letters an avatar falls back to: the first letter of the first `max` words. Two is the rule
 * for every face 24 px and up; the 20 px face takes one, because two 13 px letters in a 20 px square
 * are a smear, and a row of them reads as noise rather than as people (D-05 D1).
 */
export function initials(name: string, max = 2): string {
	const words = name.trim().split(/\s+/).filter(Boolean)
	const letters = words.slice(0, max).map((word) => [...word][0] ?? "")
	return letters.join("").toUpperCase()
}

/**
 * Whether a person matches what was typed. The comparison is case-insensitive over the name and the
 * handle, and a leading "@" is ignored so that typing a mention as it is written finds the account.
 */
export function matchesPerson(user: PublicUser, query: string): boolean {
	const needle = query.trim().toLowerCase().replace(/^@/, "")
	if (needle === "") return true
	return (
		user.handle.toLowerCase().includes(needle) ||
		user.displayName.toLowerCase().includes(needle) ||
		(user.fullName ?? "").toLowerCase().includes(needle)
	)
}

/** A directory's order: by display name, with the handle deciding a tie so it never wobbles. */
export function byName<T extends PublicUser>(users: T[]): T[] {
	return [...users].sort(
		(a, b) => a.displayName.localeCompare(b.displayName) || a.handle.localeCompare(b.handle),
	)
}

/**
 * The accounts a group may hold: live humans only. A bot has no mailbox and a deactivated account is
 * never expanded, so the server refuses both with `error.account.notMentionable` (spec 01 §11) —
 * offering them in a picker would be offering a refusal.
 */
export function isMentionable(user: PublicUser): boolean {
	return user.kind !== "bot" && user.state !== "deactivated"
}
